import './App.css';
import React, { useEffect, useRef } from 'react';
import { Routes, Route, Navigate, Outlet, useNavigate } from "react-router-dom";
import ProtectedRoute from './RoutesHelper/ProtectedRoute';
import NoSleep from 'nosleep.js';

import LoginPage from './Views/Login/LoginPage';
import Dashboard from './Views/Dashboard/Dashboard';
import SignUpPage from './Views/Login/SignUpPage';
import Counter from './Views/Dashboard/Counter/Counter';
import WorkspaceManagement from './Views/Dashboard/Management/WorkspaceManagement';
import SendMail from './Views/PasswordReset/SendMail';
import ResetWithToken from './Views/PasswordReset/ResetWithToken';
import MainLayout from './Views/Layout/MainLayout';
import PaymentTerminal from './Views/Dashboard/Payment/PaymentTerminal';
import PaymentValidator from './Helper/PaymentValidator';
import AdminPanel from './Views/Admin/AdminPanel';
import LoginLayout from './Views/Layout/LoginLayout';
import SubscriptionHandler from './Views/Subscriptions/SubscriptionHandler';
import MainPage from './Views/Welcome/MainPage';
import DSE from './Components/DSE';
import Instructions from './Views/Instructions/Instructions';
import UI from './Views/Instructions/UI';
import KitchenMonitor from './Views/Instructions/KitchenMonitor';
import Settings from './Views/Instructions/Settings/Settings';
import CreateCategory from './Views/Instructions/Settings/CreateCategory';
import CreateProduct from './Views/Instructions/Settings/CreateProduct';
import CreateExtra from './Views/Instructions/Settings/CreateExtra';
import CreateOffer from './Views/Instructions/Settings/CreateOffer';
import CreateCoupon from './Views/Instructions/Settings/CreateCoupon';
import CreateWorker from './Views/Instructions/Settings/CreateWorker';
import EditWorkspaceDemo from './Views/Instructions/Settings/EditWorkspaceDemo';
import StatisticInstructions from './Views/Instructions/StatisticInstructions';
import { Inventory } from './Views/Dashboard/Inventory/Inventory';
import { WebSocketRoute } from './RoutesHelper/WebSocketRoute';
import OrderDisplay from './Views/Dashboard/Orders/OrderDisplay';
import { StatisticDashboard } from './Views/Dashboard/Statistic/StatisticDashboard';
import { CashAccounting } from './Views/Dashboard/CashAccounting/CashAccounting';
import { initPeerInitSync } from "./Services/PeerInitSyncManager"
import PeerConnectionManager from './Services/PeerConnectionManager';
import { dbEvents } from './db/dbEvents';
import { usePeerConnection } from "./Context/PeerConnectionContext";
import Bonview from './Views/Bon/Bonview';
function App() {

  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  if (token) {
    if (
      window.location.pathname === "/app/login" ||
      window.location.pathname === "/app/register"
    ) {
      navigate("/app/dashboard");
    }
  }

  //No sleep is needed to let the device stay awake
  const noSleepRef = useRef(null);

  // Start init
  useEffect(() => {
    initPeerInitSync();
  }, []);

  // event Trigger from Host to clients
  const { isConnected, isHost } = usePeerConnection();
  function triggerClientDBReload(store) {
    PeerConnectionManager.broadcast({
      type: "trigger_reload",
      store
    });
  }

  useEffect(() => {
    const handleChange = (e) => {
      triggerClientDBReload(e.detail.store);
    };

    if (isConnected && isHost) {
      dbEvents.addEventListener("dbChange", handleChange);
    }

    return () => {
      dbEvents.removeEventListener("dbChange", handleChange);
    };
  }, [isConnected, isHost]);
  /* Checks each reload / redirect if user is logged in */
  useEffect(() => {
    const noSleepInstance = new NoSleep();
    noSleepRef.current = noSleepInstance;

    const enableNoSleepAndFullscreen = () => {
      noSleepInstance.enable();

      const docEl = document.documentElement;
      const requestFullScreen =
        docEl.requestFullscreen ||
        docEl.mozRequestFullScreen ||
        docEl.webkitRequestFullscreen ||
        docEl.msRequestFullscreen;

      if (requestFullScreen && !document.fullscreenElement) {
        requestFullScreen.call(docEl).catch((err) => {
          console.warn("Fullscreen request failed:", err);
        });
      }

      document.removeEventListener("click", enableNoSleepAndFullscreen);
    };

    if (isMobileDevice()) {
      document.addEventListener("click", enableNoSleepAndFullscreen);
    }

  }, [navigate]);

  const isMobileDevice = () => {
    return /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  };

  return (
    <Routes>
      <Route path="/" element={<Layout />}>
        <Route index element={<MainPage />} />
        <Route path="/datenschutz" element={<DSE />} />
        <Route path="/bon/:workspaceId/:offlineId" element={<Bonview />} />
        <Route path="/app/">
          {/* Open Pages */}
          <Route element={<LoginLayout />}>
            <Route path="login" element={<LoginPage />} />
            <Route path="register" element={<SignUpPage />} />
            <Route path="send_reset" element={<SendMail />} />
            <Route path="reset_password/:token" element={<ResetWithToken />} />
          </Route>

          {/* When logged in without standart Layout*/}
          <Route element={<WebSocketRoute><ProtectedRoute /></WebSocketRoute>}>
            <Route path="kasse" element={<Counter />} />
            <Route path="orders" element={<OrderDisplay />} />
            <Route path="payment" element={<PaymentTerminal />} />
            <Route path="paymentvalidator" element={<PaymentValidator />} />
          </Route>

          {/* When logged in with standart Layout*/}
          <Route element={<WebSocketRoute><MainLayout /></WebSocketRoute>}>
            <Route path='success' element={<SubscriptionHandler success={true} />} />
            <Route path='cancel' element={<SubscriptionHandler />} />
            <Route element={<ProtectedRoute />}>
              <Route index element={<Dashboard />} />
              <Route path="dashboard" element={<Dashboard />} />
            </Route>
            <Route element={<ProtectedRoute needActiveWorkspace={false} />}>
              <Route path="help/">
                <Route index element={<Instructions />} />
                <Route path='ui' element={<UI />} />
                <Route path='kitchen' element={<KitchenMonitor />} />
                <Route path='statistic' element={<StatisticInstructions />} />
                <Route path='settings/'>
                  <Route index element={<Settings />} />
                  <Route path="categories" element={<CreateCategory />} />
                  <Route path="products" element={<CreateProduct />} />
                  <Route path="extras" element={<CreateExtra />} />
                  <Route path="offers" element={<CreateOffer />} />
                  <Route path="coupons" element={<CreateCoupon />} />
                  <Route path="workers" element={<CreateWorker />} />
                  <Route path="workspace" element={<EditWorkspaceDemo />} />
                </Route>
              </Route>
            </Route>

            {/* Roles required */}
            <Route element={<ProtectedRoute needRole={true} needActiveWorkspace={false} />}>
              <Route path="settings" element={<WorkspaceManagement />} />
              <Route path="statistics" element={<StatisticDashboard />} />
            </Route>

            <Route element={<ProtectedRoute needRole={true} />}>
              <Route path="inventory" element={<Inventory />} />
              <Route path='cash_accounting' element={<CashAccounting />} />
            </Route>

            {/* Just with admin */}
            <Route element={<ProtectedRoute needRole={true} specificRole={"ADMIN"} needActiveWorkspace={false} />}>
              <Route path="admin_panel" element={<AdminPanel />} />
            </Route>
          </Route>

          {/* Redirect rest to dashboard */}
          <Route path="*" element={<Navigate to={"/app/dashboard"} />} />
        </Route>
        <Route path="*" element={<Navigate to={"/"} />} />
      </Route>
    </Routes>
  );
}

/* Main Layout */
function Layout() {
  return (
    <div className="flex bg-[#D9D9D9] overflow-hidden h-dvh w-full" >
      <Outlet />
    </div>
  )
}

export default App;
